<template>
  <div id="app">
    <HgLayout
      :baseUrl="baseUrl"
      :currentLanguage="locale"
      :currentRoute="route"
      :getLanguageToLink="getLanguageSwitchLink"
      :isWhiteLabelSolution="isLayoutSolutionActive"
      @trackFooterClick="handleTrackFooterClick"
    >
      <template #headerMetaAction>
        <HgMainNavItem
          :currentRoute="route"
          :baseUrl="baseUrl"
          :bordered="true"
          :menuItem="tm('$hg.nav.harmonize.newListingMenuItem')"
        />
      </template>
      <template #headerMetaNavigation>
        <FavouritesLink :menuItem="tm('$hg.nav.favoriteMenuItem')" menuNavItem="HgMainNavItem" />
        <HgMainNavItem
          :baseUrl="baseUrl"
          :icon="Icon.PersonOutline"
          :currentRoute="route"
          :menuItem="loggedInUserMenuItem as MenuItem"
          @mainNavItemClick="handleMainNavTracking"
          @dropDownMenuItemClick="handleMainNavTracking"
        ></HgMainNavItem>
      </template>
      <template #sideMenuMetaNavigation>
        <FavouritesLink
          :menuItem="tm('$hg.nav.favoriteMenuItem')"
          menuNavItem="HgSideMenuNavItem"
          @sideMenuItemClick="handleMobileMenuNavTracking"
        />
        <template v-if="isPrivateLoggedInUser">
          <HgSideMenuNavItem
            :menuItem="tm('$hg.nav.harmonize.overviewMenuItem')"
            :icon="Icon.PersonOutline"
            :currentRoute="route"
            :baseUrl="accountsDomainOrigin"
            @sideMenuItemClick="handleMobileMenuNavTracking"
          />
          <HgSideMenuNavItem
            :menuItem="tm('$hg.nav.harmonize.settingsMenuItem')"
            :icon="Icon.Settings"
            :currentRoute="route"
            :baseUrl="accountsDomainOrigin"
            @sideMenuItemClick="handleMobileMenuNavTracking"
          />
          <HgSideMenuNavItem
            :menuItem="tm('$hg.nav.harmonize.myAdsMenuItem')"
            :icon="Icon.Appointment"
            :currentRoute="route"
            :baseUrl="baseUrl"
            @sideMenuItemClick="handleMobileMenuNavTracking"
          />
          <HgSideMenuNavItem
            :menuItem="tm('$hg.nav.harmonize.myRealEstateMenuItem')"
            :icon="Icon.House"
            :currentRoute="route"
            :baseUrl="baseUrl"
            @sideMenuItemClick="handleMobileMenuNavTracking"
          />
          <HgSideMenuNavItem
            :menuItem="tm('$hg.nav.harmonize.searchAlertMenuItem')"
            :icon="Icon.Bell"
            :currentRoute="route"
            :baseUrl="baseUrl"
            @sideMenuItemClick="handleMobileMenuNavTracking"
          />
          <HgSideMenuNavItem
            v-if="!shouldHideMyTenantPlusMenuItem"
            :menuItem="tm('$hg.nav.harmonize.tenantPlusMenuItem')"
            :icon="Icon.PlusMembership"
            :currentRoute="route"
            :baseUrl="baseUrl"
            @sideMenuItemClick="handleMobileMenuNavTracking"
          />
          <HgSideMenuNavItem
            :menuItem="tm('$hg.nav.harmonize.signOutMenuItem')"
            :icon="Icon.SignOut"
            :currentRoute="route"
            :baseUrl="accountsDomainOrigin"
            @sideMenuItemClick="handleMobileMenuNavTracking"
          />
        </template>
        <template v-else>
          <HgSideMenuNavItem
            :baseUrl="baseUrl"
            :currentRoute="route"
            :icon="Icon.PersonOutline"
            :menuItem="tm('$hg.nav.harmonize.signInMenuItem')"
            @sideMenuItemClick="handleMobileMenuNavTracking"
          />
        </template>
      </template>
      <div class="layout-container">
        <router-view />
      </div>
    </HgLayout>
  </div>
</template>

<script setup lang="ts">
import FavouritesLink from '@/components/FavouritesLink/FavouritesLink.vue';
import { useApp } from '@/composables/app/app';
import { useLayoutSolution } from '@/composables/layout-solution/layout-solution-composable';
import { isPrivateLoggedInUser as checkIsPrivateLoggedInUser } from '@/helpers/Authentication/authClient';
import { isHgPlatform } from '@/helpers/envService';
import {
  HgLayout,
  HgMainNavItem,
  HgSideMenuNavItem,
  Icon,
  type MenuItem,
} from '@smg-real-estate/domus';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { isConsentGranted, splitIoHandler } from './helpers/SplitIoService/splitIoHandler';

const { t, tm, locale } = useI18n();
const { isLayoutSolutionActive } = useLayoutSolution();
const {
  handleTrackFooterClick,
  handleTrackNavItemClick,
  handleMainNavTracking,
  handleMobileMenuNavTracking,
  getLanguageSwitchLink,
  baseUrl,
} = useApp();
const route = useRoute();

const isPrivateLoggedInUser = ref(false);
const accountsDomainOrigin = ref(import.meta.env.VUE_APP_PLATFORM_ACCOUNT_BASE_URL);
const sourceParameter = route.query?.source as string;
const isTenantPlusChParameter = route.query?.tenantPlusCh as string;
const SOURCE_PARAM_KEY = 'hgSourceParameter';
const ANDROID_WEBVIEW_SOURCE = 'ANDROID_WEBVIEW';
const canUseSessionStorage = (): boolean => {
  return globalThis.window !== undefined && globalThis.window.sessionStorage !== undefined;
};
const myAccountDropdown = computed<object>(() => {
  const navItemsUsingBaseUrl = ['myAds', 'myRealEstate', 'searchAlertSettings'];
  let privateItems = JSON.parse(JSON.stringify(tm('$hg.nav.privateItems')));

  if (Array.isArray(privateItems)) {
    privateItems = privateItems.map((item) => {
      return {
        ...item,
        href: navItemsUsingBaseUrl.includes(item.trackingLabel)
          ? `${baseUrl}${item.href}`
          : `${import.meta.env.VUE_APP_PLATFORM_ACCOUNT_BASE_URL}${item.href}`,
      };
    });
  }

  return {
    label: t('$hg.nav.loggedInMenuItem.label'),
    children: privateItems,
  };
});

const userAccountMenuItem = computed<MenuItem>(() => ({
  label: t('$hg.nav.harmonize.overviewMenuItem.label') as string,
  href: `${import.meta.env.VUE_APP_PLATFORM_ACCOUNT_BASE_URL}/${locale.value}/overview`,
}));

const loggedInUserMenuItem = computed<MenuItem | object>(() => {
  return isPrivateLoggedInUser.value ? myAccountDropdown.value : userAccountMenuItem.value;
});

onMounted(() => {
  if (isHgPlatform()) {
    if (sourceParameter && canUseSessionStorage()) {
      globalThis.window.sessionStorage.setItem(SOURCE_PARAM_KEY, sourceParameter as string);
    }
    const loginCheckPromise = checkIsPrivateLoggedInUser();
    if (loginCheckPromise) {
      loginCheckPromise.then((isLoggedIn) => {
        isPrivateLoggedInUser.value = isLoggedIn;
      });
    }
  }

  if (isConsentGranted()) {
    splitIoHandler();
  }
});

const getSourceFromStorage = (): string | null => {
  if (!canUseSessionStorage()) {
    return null;
  }
  return globalThis.window.sessionStorage.getItem(SOURCE_PARAM_KEY);
};
const shouldHideMyTenantPlusMenuItem = computed(() => {
  const source = sourceParameter || getSourceFromStorage();
  return source === ANDROID_WEBVIEW_SOURCE && isTenantPlusChParameter !== 'true';
});
</script>

<style lang="scss">
@import './assets/globals-hg';
</style>
